// Combat magic HUD: the mana bar and the Q E R F spell bar (click a slot to choose its spell).
// Casting sends the cursor's world position; the server checks mana, range and cooldowns.
import { SPELL_KEYS, SPELLS, isSpellId, type SpellId } from '../../shared/spells.ts';
import type { Net } from './net.ts';

const $ = (id: string) => document.getElementById(id)!;

export class SpellBar {
  private known: SpellId[] = [];
  private bar: (SpellId | null)[] = [null, null, null, null];
  private slots: HTMLElement[] = [];
  private picker: HTMLElement;
  private root: HTMLElement;

  constructor(private net: Net, private aim: () => { x: number; y: number }) {
    const mana = document.createElement('div');
    mana.id = 'mana';
    mana.innerHTML = '<div id="mana-ward"></div><div id="mana-fill"></div><span id="mana-text"></span>';
    $('hp').after(mana);
    this.root = document.createElement('div');
    this.root.id = 'spellbar';
    this.root.className = 'hidden';
    SPELL_KEYS.forEach((key, i) => {
      const el = document.createElement('div');
      el.className = 'spell';
      el.innerHTML = `<span class="key">${key}</span><span class="name"></span><div class="cd"></div>`;
      el.onclick = (e) => {
        e.stopPropagation();
        this.pick(i);
      };
      this.root.append(el);
      this.slots.push(el);
    });
    this.picker = document.createElement('div');
    this.picker.id = 'spell-picker';
    this.picker.className = 'hidden';
    document.getElementById('hud')!.append(this.root, this.picker);
    document.addEventListener('click', () => this.picker.classList.add('hidden'));
    document.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || e.repeat) return;
      const i = SPELL_KEYS.indexOf(e.key.toUpperCase());
      if (i < 0 || !this.bar[i]) return;
      const at = this.aim();
      this.net.send({ t: 'cast', i, x: Math.round(at.x), y: Math.round(at.y) });
    });
  }

  setBook(known: string[], bar: (string | null)[]): void {
    this.known = known.filter(isSpellId);
    this.bar = [0, 1, 2, 3].map((i) => (isSpellId(bar[i]) ? (bar[i] as SpellId) : null));
    this.root.classList.toggle('hidden', !this.known.length);
    this.bar.forEach((id, i) => {
      const el = this.slots[i];
      const s = id ? SPELLS[id] : null;
      el.querySelector('.name')!.textContent = s ? s.name : '';
      el.style.borderColor = s ? s.color : '';
      el.style.background = s ? `radial-gradient(circle at 50% 40%, ${s.color}55, #0d0f0ccc 70%)` : '';
      el.title = s ? `${s.name} (${SPELL_KEYS[i]}): ${s.text} ${s.lp ? `${s.lp} life essence` : `${s.mana} mana`}, ${s.cooldown}s cooldown. Click to change.` : 'Click to put a spell here.';
    });
  }

  /** From the snapshot. */
  update(mana: number, max: number, ward: number, cds: number[]): void {
    $('mana-fill').style.width = `${max ? Math.min(100, (mana / max) * 100) : 0}%`;
    $('mana-ward').style.width = ward ? `${Math.min(100, (ward / 40) * 100)}%` : '0';
    $('mana-text').textContent = `Mana ${mana} / ${max}${ward ? ` · ward ${ward}` : ''}`;
    this.bar.forEach((id, i) => {
      const el = this.slots[i];
      const cd = cds[i] ?? 0;
      const s = id ? SPELLS[id] : null;
      (el.querySelector('.cd') as HTMLElement).style.height = s && cd > 0 ? `${Math.min(100, (cd / s.cooldown) * 100)}%` : '0';
      el.classList.toggle('dim', Boolean(s && s.mana > mana));
    });
  }

  private pick(i: number): void {
    if (!this.known.length) return;
    this.picker.innerHTML = '';
    const opt = (id: SpellId | null) => {
      const b = document.createElement('div');
      b.className = 'opt';
      b.textContent = id ? SPELLS[id].name : '(empty)';
      if (id) b.style.color = SPELLS[id].color;
      b.onclick = (e) => {
        e.stopPropagation();
        this.net.send({ t: 'spellBar', i, id });
        this.picker.classList.add('hidden');
      };
      this.picker.append(b);
    };
    for (const id of this.known) opt(id);
    opt(null);
    const r = this.slots[i].getBoundingClientRect();
    this.picker.style.left = `${r.left}px`;
    this.picker.style.bottom = `${window.innerHeight - r.top + 6}px`;
    this.picker.classList.remove('hidden');
  }
}
